import React, { useState, useEffect, useRef } from 'react';
import { resourcesAPI, adminAPI, emailListsAPI, API_BASE_URL } from '../../services/api';
import { usePreview } from '../../contexts/PreviewContext';
import BrandVisibilityControl, { deriveVisibilityMode } from './BrandVisibilityControl';

const ResourcesEditor = () => {
  const { isPreviewMode, exitPreview } = usePreview();
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});

  // Available brands for targeting
  const [availableBrands, setAvailableBrands] = useState([]);

  // Available email lists
  const [emailLists, setEmailLists] = useState([]);

  // Preview as client
  const [previewDomain, setPreviewDomain] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Category form
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    icon: 'file-text',
    visibility_mode: 'all',
    for_all_brands: true,
    target_domains: []
  });

  // Folder form
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [folderFormCategory, setFolderFormCategory] = useState(null);
  const [folderFormParent, setFolderFormParent] = useState(null);  // Parent folder for subfolders
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderForm, setFolderForm] = useState({
    name: '',
    visibility_mode: 'all',
    for_all_brands: true,
    target_domains: []
  });

  // Item form
  const [showItemForm, setShowItemForm] = useState(false);
  const [itemFormCategory, setItemFormCategory] = useState(null);
  const [itemFormFolder, setItemFormFolder] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    title: '',
    description: '',
    item_type: 'file',
    external_url: '',
    create_announcement: true,
    visibility_mode: 'all',
    for_all_brands: true,
    target_domains: [],
    email_list_ids: [],
    is_public: false,
    cpd_eligible: false,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Bulk upload state
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploadCategory, setBulkUploadCategory] = useState(null);
  const [bulkUploadFolder, setBulkUploadFolder] = useState(null);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ uploaded: 0, total: 0 });
  const [bulkForm, setBulkForm] = useState({
    create_announcement: true,
    visibility_mode: 'all',
    for_all_brands: true,
    target_domains: [],
    email_list_ids: [],
    is_public: false,
  });
  const bulkFileInputRef = useRef(null);

  // Bulk delete state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Bulk move state
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [bulkMoving, setBulkMoving] = useState(false);
  const [moveDestination, setMoveDestination] = useState({ categoryId: null, folderId: null });

  const iconOptions = [
    { value: 'file-text', label: 'Document' },
    { value: 'folder', label: 'Folder' },
    { value: 'archive', label: 'Archive' },
    { value: 'book', label: 'Book' },
    { value: 'briefcase', label: 'Briefcase' },
    { value: 'building', label: 'Building' },
    { value: 'calendar', label: 'Calendar' },
    { value: 'bar-chart', label: 'Bar Chart' },
    { value: 'pie-chart', label: 'Pie Chart' },
    { value: 'trending-up', label: 'Trending Up' },
    { value: 'clipboard', label: 'Clipboard' },
    { value: 'dollar-sign', label: 'Finance' },
    { value: 'globe', label: 'Globe' },
    { value: 'layers', label: 'Layers' },
    { value: 'lock', label: 'Lock' },
    { value: 'mail', label: 'Mail' },
    { value: 'search', label: 'Search' },
    { value: 'shield', label: 'Shield' },
    { value: 'star', label: 'Star' },
    { value: 'users', label: 'Users' },
    { value: 'video', label: 'Video' },
    { value: 'image', label: 'Image' },
    { value: 'link', label: 'Link' },
    { value: 'settings', label: 'Settings' },
  ];

  useEffect(() => {
    fetchCategories();
    fetchBrands();
    fetchEmailLists();
  }, []);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const data = await resourcesAPI.adminGetCategories();
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setMessage({ type: 'error', text: 'Failed to load categories' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const data = await adminAPI.getAllowedDomains();
      // Extract active domain names, excluding internal drummond domains
      const brands = data
        .filter(d => d.is_active && d.domain && !d.domain.includes('drummond'))
        .map(d => d.domain);
      setAvailableBrands(brands);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const fetchEmailLists = async () => {
    try {
      const data = await emailListsAPI.getLists();
      setEmailLists(data.filter(l => l.is_active));
    } catch (err) {
      console.error('Error fetching email lists:', err);
    }
  };

  const handlePreviewAsClient = async (domain) => {
    setPreviewDomain(domain);
    if (!domain) {
      setPreviewData(null);
      return;
    }

    setPreviewLoading(true);
    try {
      const data = await resourcesAPI.previewAsClient(domain);
      setPreviewData(data);
    } catch (err) {
      console.error('Error fetching preview:', err);
      setMessage({ type: 'error', text: 'Failed to load preview' });
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSeedCategories = async () => {
    try {
      const result = await resourcesAPI.seedCategories();
      setMessage({ type: 'success', text: result.message });
      fetchCategories();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to seed categories' });
    }
  };

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: categoryForm.name,
        icon: categoryForm.icon,
        visibility_mode: categoryForm.visibility_mode,
        for_all_brands: categoryForm.visibility_mode === 'all',
        target_domains: categoryForm.visibility_mode === 'all' ? [] : categoryForm.target_domains,
      };

      if (editingCategory) {
        await resourcesAPI.updateCategory(editingCategory.id, payload);
        setMessage({ type: 'success', text: 'Category updated successfully' });
      } else {
        await resourcesAPI.createCategory(payload);
        setMessage({ type: 'success', text: 'Category created successfully' });
      }
      setShowCategoryForm(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', icon: 'file-text', visibility_mode: 'all', for_all_brands: true, target_domains: [] });
      fetchCategories();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorText = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg).join('; ') : 'Failed to save category';
      setMessage({ type: 'error', text: errorText });
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      icon: category.icon || 'file-text',
      visibility_mode: deriveVisibilityMode(category),
      for_all_brands: category.for_all_brands ?? true,
      target_domains: category.target_domains || []
    });
    setShowCategoryForm(true);
  };

  const handleCategoryBrandToggle = (domain) => {
    setCategoryForm(prev => {
      const newDomains = prev.target_domains.includes(domain)
        ? prev.target_domains.filter(d => d !== domain)
        : [...prev.target_domains, domain];
      return { ...prev, target_domains: newDomains };
    });
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category and all its items?')) return;
    try {
      await resourcesAPI.deleteCategory(categoryId);
      setMessage({ type: 'success', text: 'Category deleted successfully' });
      fetchCategories();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete category' });
    }
  };

  // Category reorder handler
  const handleMoveCategoryOrder = async (index, direction) => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= categories.length) return;
    const newCategories = [...categories];
    [newCategories[index], newCategories[swapIndex]] = [newCategories[swapIndex], newCategories[index]];
    setCategories(newCategories);
    try {
      await resourcesAPI.reorderCategories(
        newCategories.map((cat, i) => ({ id: cat.id, display_order: i }))
      );
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to reorder categories' });
      fetchCategories();
    }
  };

  // Optimistically apply a sibling reorder into the nested categories tree (no re-fetch, no
  // loading flash). `kind` is 'folder' or 'item'; ids are globally unique per kind, so we find
  // the exact array to replace by looking for the moved element's id. Each replaced element
  // keeps its own subtree (items/subfolders) intact.
  const applyReorderToTree = (cats, movedId, kind, reordered) => {
    const ordered = reordered.map((el, i) => ({ ...el, display_order: i }));
    const arrHas = (arr) => Array.isArray(arr) && arr.some(el => el.id === movedId);
    const mapItems = (items) => (kind === 'item' && arrHas(items) ? ordered : items);
    const mapFolders = (folders) => {
      if (!Array.isArray(folders)) return folders;
      if (kind === 'folder' && arrHas(folders)) return ordered;
      return folders.map(f => ({ ...f, items: mapItems(f.items), subfolders: mapFolders(f.subfolders) }));
    };
    return cats.map(cat => ({ ...cat, items: mapItems(cat.items), folders: mapFolders(cat.folders) }));
  };

  // Reorder folders among their siblings (same category / same parent). Update state in place
  // for an instant swap, then persist; only re-fetch to roll back on failure.
  const handleMoveFolder = async (siblings, index, direction) => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= siblings.length) return;
    const arr = [...siblings];
    [arr[index], arr[swapIndex]] = [arr[swapIndex], arr[index]];
    const movedId = siblings[index].id;
    setCategories(prev => applyReorderToTree(prev, movedId, 'folder', arr));
    try {
      await resourcesAPI.reorderFolders(arr.map((f, i) => ({ id: f.id, display_order: i })));
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to reorder folders' });
      fetchCategories();
    }
  };

  // Reorder items among their siblings (same folder, or category root)
  const handleMoveItem = async (siblings, index, direction) => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= siblings.length) return;
    const arr = [...siblings];
    [arr[index], arr[swapIndex]] = [arr[swapIndex], arr[index]];
    const movedId = siblings[index].id;
    setCategories(prev => applyReorderToTree(prev, movedId, 'item', arr));
    try {
      await resourcesAPI.reorderItems(arr.map((it, i) => ({ id: it.id, display_order: i })));
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to reorder items' });
      fetchCategories();
    }
  };

  // Folder handlers
  const handleAddFolder = (category, parentFolder = null) => {
    setFolderFormCategory(category);
    setFolderFormParent(parentFolder);
    setEditingFolder(null);
    // Inherit permissions from parent folder if creating subfolder
    if (parentFolder) {
      setFolderForm({
        name: '',
        visibility_mode: deriveVisibilityMode(parentFolder),
        for_all_brands: parentFolder.for_all_brands,
        target_domains: parentFolder.target_domains || []
      });
    } else {
      setFolderForm({ name: '', visibility_mode: 'all', for_all_brands: true, target_domains: [] });
    }
    setShowFolderForm(true);
  };

  const handleEditFolder = (folder, category) => {
    setFolderFormCategory(category);
    setFolderFormParent(null);  // Not creating subfolder when editing
    setEditingFolder(folder);
    setFolderForm({
      name: folder.name,
      visibility_mode: deriveVisibilityMode(folder),
      for_all_brands: folder.for_all_brands ?? true,
      target_domains: folder.target_domains || []
    });
    setShowFolderForm(true);
  };

  const handleFolderBrandToggle = (domain) => {
    setFolderForm(prev => {
      const newDomains = prev.target_domains.includes(domain)
        ? prev.target_domains.filter(d => d !== domain)
        : [...prev.target_domains, domain];
      return { ...prev, target_domains: newDomains };
    });
  };

  const handleFolderSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: folderForm.name,
        visibility_mode: folderForm.visibility_mode,
        for_all_brands: folderForm.visibility_mode === 'all',
        target_domains: folderForm.visibility_mode === 'all' ? [] : folderForm.target_domains,
      };

      if (editingFolder) {
        await resourcesAPI.updateFolder(editingFolder.id, payload);
        setMessage({ type: 'success', text: 'Folder updated successfully' });
      } else {
        await resourcesAPI.createFolder({
          category_id: folderFormCategory.id,
          parent_id: folderFormParent?.id || null,  // Include parent_id for subfolders
          ...payload
        });
        setMessage({ type: 'success', text: folderFormParent ? 'Subfolder created successfully' : 'Folder created successfully' });
      }
      setShowFolderForm(false);
      setEditingFolder(null);
      setFolderFormParent(null);
      setFolderForm({ name: '', visibility_mode: 'all', for_all_brands: true, target_domains: [] });
      fetchCategories();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorText = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg).join('; ') : 'Failed to save folder';
      setMessage({ type: 'error', text: errorText });
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Only empty folders can be deleted; move or delete its items first.')) return;
    try {
      await resourcesAPI.deleteFolder(folderId);
      setMessage({ type: 'success', text: 'Folder deleted successfully' });
      fetchCategories();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Failed to delete folder' });
    }
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  // Item handlers
  const handleAddItem = (category, folder = null) => {
    setItemFormCategory(category);
    setItemFormFolder(folder);
    setEditingItem(null);
    setItemForm({
      title: '',
      description: '',
      item_type: 'file',
      external_url: '',
      create_announcement: true,
      visibility_mode: 'all',
      for_all_brands: true,
      target_domains: [],
      email_list_ids: [],
      is_public: false,
      cpd_eligible: false,
    });
    setSelectedFile(null);
    setShowItemForm(true);
  };

  const handleEditItem = (item, category) => {
    setItemFormCategory(category);
    setEditingItem(item);
    setItemForm({
      title: item.title,
      description: item.description || '',
      item_type: item.item_type,
      external_url: item.external_url || '',
      visibility_mode: deriveVisibilityMode(item),
      for_all_brands: item.for_all_brands ?? true,
      target_domains: item.target_domains || [],
      is_public: item.is_public ?? false,
      cpd_eligible: item.cpd_eligible ?? false,
    });
    setShowItemForm(true);
  };

  const handleBrandToggle = (domain) => {
    setItemForm(prev => {
      const newDomains = prev.target_domains.includes(domain)
        ? prev.target_domains.filter(d => d !== domain)
        : [...prev.target_domains, domain];
      return { ...prev, target_domains: newDomains };
    });
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingItem) {
        // Update existing item
        await resourcesAPI.updateItem(editingItem.id, {
          title: itemForm.title,
          description: itemForm.description,
          external_url: itemForm.item_type === 'link' ? itemForm.external_url : null,
          visibility_mode: itemForm.visibility_mode,
          for_all_brands: itemForm.visibility_mode === 'all',
          target_domains: itemForm.visibility_mode === 'all' ? [] : itemForm.target_domains,
          is_public: itemForm.is_public,
          cpd_eligible: itemForm.cpd_eligible,
        });
        setMessage({ type: 'success', text: 'Item updated successfully' });
      } else {
        // Create new item
        const formData = new FormData();
        formData.append('category_id', itemFormCategory.id);
        formData.append('title', itemForm.title);
        formData.append('description', itemForm.description);
        formData.append('item_type', itemForm.item_type);
        formData.append('create_announcement', itemForm.create_announcement);
        formData.append('send_email', itemForm.email_list_ids.length > 0);
        formData.append('visibility_mode', itemForm.visibility_mode);
        formData.append('for_all_brands', itemForm.visibility_mode === 'all');
        if (itemForm.email_list_ids.length > 0) {
          formData.append('email_list_ids', JSON.stringify(itemForm.email_list_ids));
        }
        formData.append('is_public', itemForm.is_public);
        formData.append('cpd_eligible', itemForm.cpd_eligible);

        // Add target_domains (whitelist or blacklist members) unless visible to all
        if (itemForm.visibility_mode !== 'all' && itemForm.target_domains.length > 0) {
          formData.append('target_domains', JSON.stringify(itemForm.target_domains));
        }

        // Add folder_id if item is being added to a folder
        if (itemFormFolder) {
          formData.append('folder_id', itemFormFolder.id);
        }

        if (itemForm.item_type === 'link') {
          formData.append('external_url', itemForm.external_url);
        } else if (selectedFile) {
          formData.append('file', selectedFile);
        } else {
          setMessage({ type: 'error', text: 'Please select a file to upload' });
          return;
        }

        await resourcesAPI.createItem(formData);
        setMessage({ type: 'success', text: 'Item created successfully' });
      }

      setShowItemForm(false);
      setItemFormCategory(null);
      setItemFormFolder(null);
      setEditingItem(null);
      fetchCategories();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorText = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg).join('; ') : 'Failed to save item';
      setMessage({ type: 'error', text: errorText });
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await resourcesAPI.deleteItem(itemId);
      setMessage({ type: 'success', text: 'Item deleted successfully' });
      fetchCategories();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete item' });
    }
  };

  // Bulk delete handlers
  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAllInCategory = (category) => {
    const allItemIds = [];
    // Get root items
    category.items?.forEach(item => allItemIds.push(item.id));
    // Get items from folders recursively
    const collectFolderItems = (folders) => {
      folders?.forEach(folder => {
        folder.items?.forEach(item => allItemIds.push(item.id));
        if (folder.subfolders) {
          collectFolderItems(folder.subfolders);
        }
      });
    };
    collectFolderItems(category.folders);

    setSelectedItems(prev => {
      const newSet = new Set(prev);
      const allSelected = allItemIds.every(id => newSet.has(id));
      if (allSelected) {
        // Deselect all
        allItemIds.forEach(id => newSet.delete(id));
      } else {
        // Select all
        allItemIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      setMessage({ type: 'error', text: 'No items selected' });
      return;
    }
    if (!window.confirm(`Delete ${selectedItems.size} selected item(s)? This cannot be undone.`)) return;

    setBulkDeleting(true);
    try {
      const response = await resourcesAPI.bulkDeleteItems(Array.from(selectedItems));
      setMessage({ type: 'success', text: response.message || `Deleted ${selectedItems.size} item(s)` });
      setSelectedItems(new Set());
      fetchCategories();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete items: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkMove = async () => {
    if (selectedItems.size === 0 || !moveDestination.categoryId) return;
    setBulkMoving(true);
    try {
      const response = await resourcesAPI.bulkMoveItems(
        Array.from(selectedItems),
        moveDestination.categoryId,
        moveDestination.folderId
      );
      setMessage({ type: 'success', text: response.message || `Moved ${selectedItems.size} item(s)` });
      setSelectedItems(new Set());
      setShowMoveModal(false);
      setMoveDestination({ categoryId: null, folderId: null });
      fetchCategories();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to move items: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setBulkMoving(false);
    }
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!itemForm.title) {
        setItemForm((prev) => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, '') }));
      }
    }
  };

  // Bulk upload handlers
  const openBulkUpload = (category, folder = null) => {
    setBulkUploadCategory(category);
    setBulkUploadFolder(folder);
    setBulkFiles([]);
    setBulkForm({
      create_announcement: true,
      visibility_mode: 'all',
      for_all_brands: true,
      target_domains: [],
      email_list_ids: [],
      is_public: false,
    });
    setBulkProgress({ uploaded: 0, total: 0 });
    setShowBulkUpload(true);
  };

  const closeBulkUpload = () => {
    setShowBulkUpload(false);
    setBulkUploadCategory(null);
    setBulkUploadFolder(null);
    setBulkFiles([]);
    setBulkUploading(false);
    setBulkProgress({ uploaded: 0, total: 0 });
  };

  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setBulkFiles(files);
  };

  const handleBulkBrandToggle = (domain) => {
    setBulkForm(prev => {
      const newDomains = prev.target_domains.includes(domain)
        ? prev.target_domains.filter(d => d !== domain)
        : [...prev.target_domains, domain];
      return { ...prev, target_domains: newDomains };
    });
  };

  const removeBulkFile = (index) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (bulkFiles.length === 0) {
      setMessage({ type: 'error', text: 'Please select files to upload' });
      return;
    }

    setBulkUploading(true);
    setBulkProgress({ uploaded: 0, total: bulkFiles.length });

    try {
      const formData = new FormData();
      formData.append('category_id', bulkUploadCategory.id);
      formData.append('create_announcement', bulkForm.create_announcement);
      formData.append('send_email', bulkForm.email_list_ids.length > 0);
      formData.append('visibility_mode', bulkForm.visibility_mode);
      formData.append('for_all_brands', bulkForm.visibility_mode === 'all');
      if (bulkForm.email_list_ids.length > 0) {
        formData.append('email_list_ids', JSON.stringify(bulkForm.email_list_ids));
      }
      formData.append('is_public', bulkForm.is_public);

      if (bulkUploadFolder) {
        formData.append('folder_id', bulkUploadFolder.id);
      }

      if (bulkForm.visibility_mode !== 'all' && bulkForm.target_domains.length > 0) {
        formData.append('target_domains', JSON.stringify(bulkForm.target_domains));
      }

      // Append all files
      bulkFiles.forEach(file => {
        formData.append('files', file);
      });

      const result = await resourcesAPI.bulkUploadItems(formData, (progressEvent) => {
        const percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        // Update progress visually based on upload percentage
        const estimatedUploaded = Math.floor((percentComplete / 100) * bulkFiles.length);
        setBulkProgress({ uploaded: estimatedUploaded, total: bulkFiles.length });
      });

      setBulkProgress({ uploaded: bulkFiles.length, total: bulkFiles.length });
      const uploadedCount = result.total_uploaded || result.uploaded?.length || 0;
      const failedCount = result.total_failed || result.failed?.length || 0;
      if (failedCount > 0) {
        setMessage({
          type: 'warning',
          text: `Uploaded ${uploadedCount} file${uploadedCount !== 1 ? 's' : ''}, ${failedCount} failed`
        });
      } else {
        setMessage({
          type: 'success',
          text: `Successfully uploaded ${uploadedCount} file${uploadedCount !== 1 ? 's' : ''}`
        });
      }
      closeBulkUpload();
      fetchCategories();
    } catch (err) {
      console.error('Bulk upload error:', err);
      const detail = err.response?.data?.detail;
      const errorText = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg).join('; ') : 'Bulk upload failed';
      setMessage({ type: 'error', text: errorText });
    } finally {
      setBulkUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Block access in global preview mode
  if (isPreviewMode) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">
          <h5>Admin features disabled in Preview Mode</h5>
          <p className="mb-3">Please exit preview mode to access admin features.</p>
          <button
            className="btn btn-primary"
            onClick={exitPreview}
          >
            Exit Preview Mode
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="resources-editor">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Resources</h4>
          <p className="text-muted mb-0">Manage document categories and uploaded files.</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          {/* Preview as Client Dropdown */}
          <div className="d-flex align-items-center">
            <label className="me-2 text-muted small">Preview as:</label>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', minWidth: '180px' }}
              value={previewDomain}
              onChange={(e) => handlePreviewAsClient(e.target.value)}
            >
              <option value="">Admin (all resources)</option>
              {availableBrands.map(domain => (
                <option key={domain} value={domain}>{domain}</option>
              ))}
            </select>
            {previewLoading && (
              <span className="spinner-border spinner-border-sm ms-2" role="status"></span>
            )}
          </div>
          {categories.length === 0 && (
            <button className="btn btn-outline-secondary" onClick={handleSeedCategories}>
              Create Default Categories
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditingCategory(null);
              setCategoryForm({ name: '', icon: 'file-text', visibility_mode: 'all', for_all_brands: true, target_domains: [] });
              setShowCategoryForm(true);
            }}
          >
            Add Category
          </button>
        </div>
      </div>

      {/* Preview Mode Banner */}
      {previewData && (
        <div className="alert alert-info d-flex justify-content-between align-items-center mb-4">
          <div>
            <strong>Preview Mode:</strong> Showing resources visible to <strong>{previewDomain}</strong>
            {' '}({previewData.item_count} items in {previewData.categories?.length || 0} categories)
          </div>
          <button
            className="btn btn-sm btn-outline-info"
            onClick={() => handlePreviewAsClient('')}
          >
            Exit Preview
          </button>
        </div>
      )}

      {/* Bulk Selection Bar */}
      {selectedItems.size > 0 && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center mb-4">
          <div>
            <strong>{selectedItems.size}</strong> item{selectedItems.size !== 1 ? 's' : ''} selected
          </div>
          <div>
            <button
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={clearSelection}
            >
              Clear Selection
            </button>
            <button
              className="btn btn-sm btn-outline-primary me-2"
              onClick={() => {
                setMoveDestination({ categoryId: null, folderId: null });
                setShowMoveModal(true);
              }}
            >
              <i className="bi bi-folder-symlink me-1"></i>
              Move ({selectedItems.size})
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
            >
              {bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedItems.size})`}
            </button>
          </div>
        </div>
      )}

      {message && (
        <div
          className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible`}
        >
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Move Items Modal */}
      {showMoveModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#072040', color: '#fff' }}>
                <h6 className="modal-title mb-0">
                  Move {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''}
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowMoveModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <p className="text-muted small mb-3">Select a destination category or folder:</p>
                {categories.map((cat) => {
                  const isSelectedCatRoot = moveDestination.categoryId === cat.id && moveDestination.folderId === null;
                  const renderFolders = (folders, depth = 1) => {
                    if (!folders || folders.length === 0) return null;
                    return folders.map((folder) => {
                      const isSelectedFolder = moveDestination.folderId === folder.id;
                      return (
                        <div key={folder.id}>
                          <div
                            className={`d-flex align-items-center py-2 px-2 rounded cursor-pointer ${
                              isSelectedFolder ? 'text-white' : ''
                            }`}
                            style={{
                              paddingLeft: `${depth * 1.5}rem`,
                              backgroundColor: isSelectedFolder ? '#072040' : 'transparent',
                              cursor: 'pointer',
                            }}
                            onClick={() => setMoveDestination({ categoryId: cat.id, folderId: folder.id })}
                          >
                            <i className="bi bi-folder me-2 flex-shrink-0"></i>
                            <span>{folder.name}</span>
                            {folder.items?.length > 0 && (
                              <span className={`badge ms-auto ${isSelectedFolder ? 'bg-light text-dark' : 'bg-secondary'}`}>
                                {folder.items.length}
                              </span>
                            )}
                          </div>
                          {folder.subfolders && renderFolders(folder.subfolders, depth + 1)}
                        </div>
                      );
                    });
                  };
                  return (
                    <div key={cat.id} className="mb-2">
                      <div
                        className={`d-flex align-items-center py-2 px-2 rounded fw-semibold cursor-pointer ${
                          isSelectedCatRoot ? 'text-white' : ''
                        }`}
                        style={{
                          backgroundColor: isSelectedCatRoot ? '#072040' : '#f8f9fa',
                          cursor: 'pointer',
                        }}
                        onClick={() => setMoveDestination({ categoryId: cat.id, folderId: null })}
                      >
                        <i className="bi bi-collection me-2 flex-shrink-0"></i>
                        <span>{cat.name}</span>
                        <span className="text-muted small ms-1">(root)</span>
                      </div>
                      {renderFolders(cat.folders)}
                    </div>
                  );
                })}
              </div>
              <div className="modal-footer d-flex justify-content-between">
                <span className="text-muted small">
                  {moveDestination.categoryId
                    ? `Destination: ${categories.find(c => c.id === moveDestination.categoryId)?.name || ''}${
                        moveDestination.folderId
                          ? ' / ' + (() => {
                              const findFolder = (folders) => {
                                for (const f of (folders || [])) {
                                  if (f.id === moveDestination.folderId) return f.name;
                                  const sub = findFolder(f.subfolders);
                                  if (sub) return sub;
                                }
                                return null;
                              };
                              const cat = categories.find(c => c.id === moveDestination.categoryId);
                              return findFolder(cat?.folders) || '';
                            })()
                          : ' (root)'
                      }`
                    : 'No destination selected'}
                </span>
                <div>
                  <button
                    className="btn btn-sm btn-outline-secondary me-2"
                    onClick={() => setShowMoveModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleBulkMove}
                    disabled={!moveDestination.categoryId || bulkMoving}
                  >
                    {bulkMoving ? 'Moving...' : 'Move Here'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">{editingCategory ? 'Edit Category' : 'New Category'}</h6>
          </div>
          <div className="card-body">
            <form onSubmit={handleCategorySubmit}>
              <div className="row">
                <div className="col-md-8 mb-3">
                  <label className="form-label">Category Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Icon</label>
                  <select
                    className="form-select"
                    value={categoryForm.icon}
                    onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                  >
                    {iconOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Brand Targeting for Category */}
              <BrandVisibilityControl
                keyPrefix="category"
                mode={categoryForm.visibility_mode || (categoryForm.for_all_brands === false ? 'include' : 'all')}
                onModeChange={(newMode) => setCategoryForm({
                  ...categoryForm,
                  visibility_mode: newMode,
                  for_all_brands: newMode === 'all',
                  target_domains: newMode === 'all' ? [] : categoryForm.target_domains,
                })}
                availableBrands={availableBrands}
                selectedDomains={categoryForm.target_domains}
                onToggle={handleCategoryBrandToggle}
              />

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowCategoryForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Folder Form Modal */}
      {showFolderForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">
              {editingFolder ? 'Edit Folder' :
               folderFormParent ? `Add Subfolder to "${folderFormParent.name}" in "${folderFormCategory?.name}"` :
               `Add Folder to "${folderFormCategory?.name}"`}
            </h6>
            {folderFormParent && !editingFolder && (
              <small className="text-muted d-block mt-1">
                Permissions inherited from parent folder
              </small>
            )}
          </div>
          <div className="card-body">
            <form onSubmit={handleFolderSubmit}>
              <div className="mb-3">
                <label className="form-label">Folder Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={folderForm.name}
                  onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
                  placeholder="e.g., 2024 Reports"
                  required
                />
              </div>

              {/* Brand Targeting for Folder */}
              <BrandVisibilityControl
                keyPrefix="folder"
                mode={folderForm.visibility_mode || (folderForm.for_all_brands === false ? 'include' : 'all')}
                onModeChange={(newMode) => setFolderForm({
                  ...folderForm,
                  visibility_mode: newMode,
                  for_all_brands: newMode === 'all',
                  target_domains: newMode === 'all' ? [] : folderForm.target_domains,
                })}
                availableBrands={availableBrands}
                selectedDomains={folderForm.target_domains}
                onToggle={handleFolderBrandToggle}
              />

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary">
                  {editingFolder ? 'Update' : 'Create Folder'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowFolderForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Item Form Modal */}
      {showItemForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">
              {editingItem ? 'Edit Item' : itemFormFolder
                ? `Add Item to "${itemFormFolder.name}" in "${itemFormCategory?.name}"`
                : `Add Item to "${itemFormCategory?.name}"`}
            </h6>
          </div>
          <div className="card-body">
            <form onSubmit={handleItemSubmit}>
              {!editingItem && (
                <div className="mb-3">
                  <label className="form-label">Type</label>
                  <div className="btn-group w-100">
                    <button
                      type="button"
                      className={`btn ${itemForm.item_type === 'file' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setItemForm({ ...itemForm, item_type: 'file' })}
                    >
                      File Upload
                    </button>
                    <button
                      type="button"
                      className={`btn ${itemForm.item_type === 'link' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setItemForm({ ...itemForm, item_type: 'link' })}
                    >
                      External Link
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={itemForm.title}
                  onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Description (optional)</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={itemForm.description}
                  onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                />
              </div>

              {itemForm.item_type === 'link' ? (
                <div className="mb-3">
                  <label className="form-label">URL</label>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://..."
                    value={itemForm.external_url}
                    onChange={(e) => setItemForm({ ...itemForm, external_url: e.target.value })}
                    required={!editingItem}
                  />
                </div>
              ) : !editingItem ? (
                <div className="mb-3">
                  <label className="form-label">File</label>
                  <input
                    type="file"
                    className="form-control"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".pdf,.xlsx,.xls,.docx,.doc,.pptx,.ppt,.csv,.png,.jpg,.jpeg,.gif,.mp4,.webm,.mov,.avi,.mkv"
                  />
                  <small className="text-muted">
                    Allowed: PDF, Excel, Word, PowerPoint, CSV, Images, Videos (max 1GB)
                  </small>
                  {selectedFile && (
                    <div className="mt-2 text-success small">
                      Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                    </div>
                  )}
                </div>
              ) : null}

              {/* Create Announcement Checkbox - only show when creating new items */}
              {!editingItem && (
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="createAnnouncement"
                    checked={itemForm.create_announcement}
                    onChange={(e) => setItemForm({ ...itemForm, create_announcement: e.target.checked, email_list_ids: e.target.checked ? itemForm.email_list_ids : [] })}
                  />
                  <label className="form-check-label" htmlFor="createAnnouncement">
                    Create announcement for this resource
                  </label>
                  <small className="text-muted d-block">
                    Uncheck to add the resource without notifying users
                  </small>
                </div>
              )}

              {/* Email List Selection - only show when creating announcement */}
              {!editingItem && itemForm.create_announcement && emailLists.length > 0 && (
                <div className="mb-3 ms-4 p-2 border rounded bg-light">
                  <label className="form-label small fw-semibold mb-1">
                    <i className="bi bi-envelope me-1"></i>
                    Email Delivery
                  </label>
                  <small className="text-muted d-block mb-2">
                    Select lists to email. Leave empty for no email.
                  </small>
                  <div className="row">
                    {emailLists.map((list) => (
                      <div className="col-md-6" key={list.id}>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`item-email-list-${list.id}`}
                            checked={itemForm.email_list_ids.includes(list.id)}
                            onChange={() => {
                              const newIds = itemForm.email_list_ids.includes(list.id)
                                ? itemForm.email_list_ids.filter(id => id !== list.id)
                                : [...itemForm.email_list_ids, list.id];
                              setItemForm({ ...itemForm, email_list_ids: newIds });
                            }}
                          />
                          <label className="form-check-label" htmlFor={`item-email-list-${list.id}`}>
                            {list.name}
                            <span className="badge bg-info ms-1" style={{ fontSize: '0.7em' }}>
                              {list.effective_member_count}
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Make Public Checkbox */}
              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isPublic"
                    checked={itemForm.is_public}
                    onChange={(e) => setItemForm({ ...itemForm, is_public: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="isPublic">
                    <strong>Make Public</strong> - Allow download without login
                  </label>
                  <small className="text-muted d-block">
                    Public resources can be accessed by anyone with the link.
                    {itemForm.is_public && (
                      <span className="text-warning d-block mt-1">
                        <i className="bi bi-exclamation-triangle"></i> Bypasses all authentication
                      </span>
                    )}
                  </small>
                </div>

                {/* Show copyable public link when editing public resource */}
                {editingItem && itemForm.is_public && (
                  <div className="mt-2 p-2 border rounded bg-light">
                    <label className="form-label small text-muted">Public Download Link:</label>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        value={`${API_BASE_URL || window.location.origin}/api/resources/download/${editingItem.id}`}
                        readOnly
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          navigator.clipboard.writeText(`${API_BASE_URL || window.location.origin}/api/resources/download/${editingItem.id}`);
                          setMessage({ type: 'success', text: 'Link copied!' });
                        }}
                      >
                        <i className="bi bi-clipboard"></i> Copy
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* CPD Eligible */}
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="cpd-eligible"
                  checked={itemForm.cpd_eligible}
                  onChange={(e) => setItemForm({ ...itemForm, cpd_eligible: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="cpd-eligible">
                  Eligible for CPD
                </label>
                <small className="text-muted d-block">This resource will appear in the CPD task builder</small>
              </div>

              {/* Brand Targeting */}
              <BrandVisibilityControl
                keyPrefix="item"
                mode={itemForm.visibility_mode || (itemForm.for_all_brands === false ? 'include' : 'all')}
                onModeChange={(newMode) => setItemForm({
                  ...itemForm,
                  visibility_mode: newMode,
                  for_all_brands: newMode === 'all',
                  target_domains: newMode === 'all' ? [] : itemForm.target_domains,
                })}
                availableBrands={availableBrands}
                selectedDomains={itemForm.target_domains}
                onToggle={handleBrandToggle}
              />

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary">
                  {editingItem ? 'Update' : 'Add Item'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowItemForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              Bulk Upload to "{bulkUploadFolder ? `${bulkUploadFolder.name}" in "${bulkUploadCategory?.name}` : bulkUploadCategory?.name}"
            </h6>
            {bulkUploading && (
              <span className="badge bg-info">
                Uploading {bulkProgress.uploaded}/{bulkProgress.total}
              </span>
            )}
          </div>
          <div className="card-body">
            <form onSubmit={handleBulkUpload}>
              {/* File Selection */}
              <div className="mb-3">
                <label className="form-label">Select Files</label>
                <input
                  type="file"
                  className="form-control"
                  ref={bulkFileInputRef}
                  onChange={handleBulkFileSelect}
                  multiple
                  accept=".pdf,.xlsx,.xls,.docx,.doc,.pptx,.ppt,.csv,.png,.jpg,.jpeg,.gif,.mp4,.webm,.mov,.avi,.mkv"
                  disabled={bulkUploading}
                />
                <small className="text-muted">
                  Select multiple files. Titles will be generated from filenames.
                </small>
              </div>

              {/* Selected Files List */}
              {bulkFiles.length > 0 && (
                <div className="mb-3">
                  <label className="form-label">Selected Files ({bulkFiles.length})</label>
                  <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {bulkFiles.map((file, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                        <div>
                          <span className="me-2">
                            {file.name.toLowerCase().endsWith('.pdf') ? '📄' :
                             file.name.toLowerCase().match(/\.(xlsx?|csv)$/) ? '📊' :
                             file.name.toLowerCase().match(/\.(docx?|pptx?)$/) ? '📝' :
                             file.name.toLowerCase().match(/\.(png|jpg|jpeg|gif)$/) ? '🖼️' :
                             file.name.toLowerCase().match(/\.(mp4|webm|mov|avi|mkv)$/) ? '🎬' : '📁'}
                          </span>
                          <span>{file.name}</span>
                          <small className="text-muted ms-2">({formatFileSize(file.size)})</small>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeBulkFile(index)}
                          disabled={bulkUploading}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Create Announcement Checkbox */}
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="bulkCreateAnnouncement"
                  checked={bulkForm.create_announcement}
                  onChange={(e) => setBulkForm({ ...bulkForm, create_announcement: e.target.checked, email_list_ids: e.target.checked ? bulkForm.email_list_ids : [] })}
                  disabled={bulkUploading}
                />
                <label className="form-check-label" htmlFor="bulkCreateAnnouncement">
                  Create announcement for uploaded files
                </label>
                <small className="text-muted d-block">
                  Creates one announcement listing all uploaded files
                </small>
              </div>

              {/* Email List Selection - only show when creating announcement */}
              {bulkForm.create_announcement && emailLists.length > 0 && (
                <div className="mb-3 ms-4 p-2 border rounded bg-light">
                  <label className="form-label small fw-semibold mb-1">
                    <i className="bi bi-envelope me-1"></i>
                    Email Delivery
                  </label>
                  <small className="text-muted d-block mb-2">
                    Select lists to email. Leave empty for no email.
                  </small>
                  <div className="row">
                    {emailLists.map((list) => (
                      <div className="col-md-6" key={list.id}>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`bulk-email-list-${list.id}`}
                            checked={bulkForm.email_list_ids.includes(list.id)}
                            onChange={() => {
                              const newIds = bulkForm.email_list_ids.includes(list.id)
                                ? bulkForm.email_list_ids.filter(id => id !== list.id)
                                : [...bulkForm.email_list_ids, list.id];
                              setBulkForm({ ...bulkForm, email_list_ids: newIds });
                            }}
                            disabled={bulkUploading}
                          />
                          <label className="form-check-label" htmlFor={`bulk-email-list-${list.id}`}>
                            {list.name}
                            <span className="badge bg-info ms-1" style={{ fontSize: '0.7em' }}>
                              {list.effective_member_count}
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Make Public Checkbox */}
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="bulkIsPublic"
                  checked={bulkForm.is_public}
                  onChange={(e) => setBulkForm({ ...bulkForm, is_public: e.target.checked })}
                  disabled={bulkUploading}
                />
                <label className="form-check-label" htmlFor="bulkIsPublic">
                  Make all files public (accessible without login)
                </label>
                <small className="text-muted d-block">
                  Public files can be downloaded by anyone with the link
                </small>
                {bulkForm.is_public && (
                  <small className="text-warning d-block">
                    <i className="bi bi-exclamation-triangle"></i> Bypasses authentication for all uploaded files
                  </small>
                )}
              </div>

              {/* Brand Targeting */}
              <BrandVisibilityControl
                keyPrefix="bulk"
                mode={bulkForm.visibility_mode || (bulkForm.for_all_brands === false ? 'include' : 'all')}
                onModeChange={(newMode) => setBulkForm({
                  ...bulkForm,
                  visibility_mode: newMode,
                  for_all_brands: newMode === 'all',
                  target_domains: newMode === 'all' ? [] : bulkForm.target_domains,
                })}
                availableBrands={availableBrands}
                selectedDomains={bulkForm.target_domains}
                onToggle={handleBulkBrandToggle}
                disabled={bulkUploading}
              />

              {/* Upload Progress */}
              {bulkUploading && (
                <div className="mb-3">
                  <div className="progress" style={{ height: '20px' }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${bulkProgress.total > 0 ? (bulkProgress.uploaded / bulkProgress.total) * 100 : 0}%` }}
                    >
                      {bulkProgress.uploaded}/{bulkProgress.total} files
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={bulkUploading || bulkFiles.length === 0}
                >
                  {bulkUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Uploading...
                    </>
                  ) : (
                    `Upload ${bulkFiles.length} File${bulkFiles.length !== 1 ? 's' : ''}`
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeBulkUpload}
                  disabled={bulkUploading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Categories List */}
      {categories.length === 0 ? (
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            <p className="mb-3">No resource categories yet.</p>
            <button className="btn btn-outline-primary" onClick={handleSeedCategories}>
              Create Default Categories
            </button>
          </div>
        </div>
      ) : (
        <div className="accordion" id="categoriesAccordion">
          {categories.map((category, catIndex) => (
            <div className="accordion-item" key={category.id}>
              <h2 className="accordion-header">
                <div className="d-flex w-100">
                  <div className="d-flex flex-column justify-content-center px-1" style={{ gap: '2px' }}>
                    <button
                      className="btn btn-sm p-0 border-0"
                      style={{ lineHeight: 1, fontSize: '0.7rem', color: catIndex === 0 ? '#ccc' : 'var(--gray-color)' }}
                      disabled={catIndex === 0}
                      onClick={(e) => { e.stopPropagation(); handleMoveCategoryOrder(catIndex, 'up'); }}
                      title="Move up"
                    >▲</button>
                    <button
                      className="btn btn-sm p-0 border-0"
                      style={{ lineHeight: 1, fontSize: '0.7rem', color: catIndex === categories.length - 1 ? '#ccc' : 'var(--gray-color)' }}
                      disabled={catIndex === categories.length - 1}
                      onClick={(e) => { e.stopPropagation(); handleMoveCategoryOrder(catIndex, 'down'); }}
                      title="Move down"
                    >▼</button>
                  </div>
                  <button
                    className={`accordion-button flex-grow-1 ${
                      expandedCategory === category.id ? '' : 'collapsed'
                    }`}
                    type="button"
                    onClick={() =>
                      setExpandedCategory(expandedCategory === category.id ? null : category.id)
                    }
                  >
                    <span className="me-2">{category.name}</span>
                    <span className="badge bg-secondary">{category.item_count} items</span>
                    {category.visibility_mode === 'exclude' ? (
                      <span className="badge bg-info text-dark ms-2" title={`Hidden from: ${category.target_domains?.join(', ')}`}>
                        🚫 {category.target_domains?.length || 0} hidden
                      </span>
                    ) : category.for_all_brands ? (
                      <span className="badge bg-success ms-2" title="Visible to all brands">All</span>
                    ) : (
                      <span className="badge bg-warning text-dark ms-2" title={category.target_domains?.join(', ')}>
                        🔒 {category.target_domains?.length || 0} brand{(category.target_domains?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    )}
                    {!category.is_active && (
                      <span className="badge bg-danger ms-2">Inactive</span>
                    )}
                  </button>
                  <div className="d-flex align-items-center px-3 border-start">
                    <button
                      className="btn btn-sm btn-outline-success me-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddFolder(category);
                      }}
                      title="Add Folder"
                    >
                      + Folder
                    </button>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddItem(category);
                      }}
                    >
                      Add Item
                    </button>
                    <button
                      className="btn btn-sm btn-outline-info me-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        openBulkUpload(category);
                      }}
                      title="Upload multiple files at once"
                    >
                      Bulk Upload
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCategory(category);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(category.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </h2>
              <div
                className={`accordion-collapse collapse ${
                  expandedCategory === category.id ? 'show' : ''
                }`}
              >
                <div className="accordion-body p-0">
                  {/* Recursive Folder Renderer */}
                  {(() => {
                    const renderFolder = (folder, depth = 0, siblings = [], index = 0) => {
                      const indent = depth * 1.5;  // rem units for indentation
                      return (
                        <div key={folder.id} className="folder-item border-bottom">
                          <div
                            className="d-flex align-items-center p-2 bg-light"
                            style={{ cursor: 'pointer', paddingLeft: `${indent + 0.5}rem` }}
                            onClick={() => toggleFolder(folder.id)}
                          >
                            <span className="me-2">{expandedFolders[folder.id] ? '📂' : '📁'}</span>
                            <strong className="me-2">{folder.name}</strong>
                            <span className="badge bg-secondary me-2">{folder.item_count} items</span>
                            {folder.subfolders?.length > 0 && (
                              <span className="badge bg-info me-2">{folder.subfolders.length} subfolder{folder.subfolders.length !== 1 ? 's' : ''}</span>
                            )}
                            {folder.visibility_mode === 'exclude' ? (
                              <span className="badge bg-info text-dark me-2" title={`Hidden from: ${folder.target_domains?.join(', ')}`}>
                                🚫 {folder.target_domains?.length || 0} hidden
                              </span>
                            ) : folder.for_all_brands ? (
                              <span className="badge bg-success me-2" title="Visible to all brands">All</span>
                            ) : (
                              <span className="badge bg-warning text-dark me-2" title={folder.target_domains?.join(', ')}>
                                🔒 {folder.target_domains?.length || 0} brand{(folder.target_domains?.length || 0) !== 1 ? 's' : ''}
                              </span>
                            )}
                            {folder.parent_id && (
                              <span className="badge bg-light text-secondary me-2" title="Subfolder">↳</span>
                            )}
                            <div className="flex-grow-1"></div>
                            <div className="btn-group btn-group-sm me-2" role="group">
                              <button className="btn btn-outline-secondary" title="Move up"
                                disabled={index === 0}
                                onClick={(e) => { e.stopPropagation(); handleMoveFolder(siblings, index, 'up'); }}>▲</button>
                              <button className="btn btn-outline-secondary" title="Move down"
                                disabled={index === siblings.length - 1}
                                onClick={(e) => { e.stopPropagation(); handleMoveFolder(siblings, index, 'down'); }}>▼</button>
                            </div>
                            <button
                              className="btn btn-sm btn-outline-success me-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddFolder(category, folder);
                              }}
                              title="Add subfolder"
                            >
                              + Subfolder
                            </button>
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddItem(category, folder);
                              }}
                            >
                              + Item
                            </button>
                            <button
                              className="btn btn-sm btn-outline-info me-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                openBulkUpload(category, folder);
                              }}
                              title="Bulk upload to this folder"
                            >
                              Bulk
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary me-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditFolder(folder, category);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteFolder(folder.id);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                          {expandedFolders[folder.id] && (
                            <>
                              {/* Render items FIRST (before subfolders) */}
                              {folder.items?.length > 0 && (
                                <table className="table table-hover mb-0">
                                  <tbody>
                                    {folder.items.map((item, ii) => (
                                      <tr key={item.id} className={`${!item.is_active ? 'table-warning' : ''} ${selectedItems.has(item.id) ? 'table-primary' : ''}`}>
                                        <td style={{ width: '40px', paddingLeft: `${indent + 2}rem` }}>
                                          <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={selectedItems.has(item.id)}
                                            onChange={() => handleSelectItem(item.id)}
                                          />
                                        </td>
                                        <td>{item.title}</td>
                                        <td>
                                          <span className={`badge ${item.item_type === 'link' ? 'bg-info' : 'bg-primary'}`}>
                                            {item.item_type}
                                          </span>
                                        </td>
                                        <td>
                                          {item.visibility_mode === 'exclude' ? (
                                            <span className="badge bg-info text-dark" title={`Hidden from: ${item.target_domains?.join(', ')}`}>
                                              🚫 {item.target_domains?.length || 0}
                                            </span>
                                          ) : item.for_all_brands ? (
                                            <span className="badge bg-success">All</span>
                                          ) : (
                                            <span className="badge bg-warning text-dark" title={item.target_domains?.join(', ')}>
                                              {item.target_domains?.length || 0}
                                            </span>
                                          )}
                                        </td>
                                        <td>
                                          {item.item_type === 'link' ? (
                                            <a href={item.external_url} target="_blank" rel="noopener noreferrer" className="small">
                                              {item.external_url?.substring(0, 30)}...
                                            </a>
                                          ) : (
                                            <span className="small">{item.file_name}</span>
                                          )}
                                        </td>
                                        <td><small className="text-muted">{formatFileSize(item.file_size)}</small></td>
                                        <td className="text-end">
                                          <div className="btn-group btn-group-sm me-2" role="group">
                                            <button className="btn btn-outline-secondary" title="Move up"
                                              disabled={ii === 0}
                                              onClick={() => handleMoveItem(folder.items, ii, 'up')}>▲</button>
                                            <button className="btn btn-outline-secondary" title="Move down"
                                              disabled={ii === folder.items.length - 1}
                                              onClick={() => handleMoveItem(folder.items, ii, 'down')}>▼</button>
                                          </div>
                                          <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => handleEditItem(item, category)}>Edit</button>
                                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteItem(item.id)}>Delete</button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}

                              {/* Render subfolders AFTER items */}
                              {folder.subfolders?.length > 0 && folder.subfolders.map((subfolder, si) => renderFolder(subfolder, depth + 1, folder.subfolders, si))}

                              {folder.items?.length === 0 && folder.subfolders?.length === 0 && (
                                <p className="text-muted p-2 mb-0 small" style={{ paddingLeft: `${indent + 2}rem` }}>No items or subfolders</p>
                              )}
                            </>
                          )}
                        </div>
                      );
                    };
                    return category.folders?.length > 0 && (
                      <div className="folders-section">
                        {category.folders.map((folder, fi) => renderFolder(folder, 0, category.folders, fi))}
                      </div>
                    );
                  })()}

                  {/* Root Items (not in any folder) */}
                  {category.items?.length === 0 && category.folders?.length === 0 ? (
                    <p className="text-muted p-3 mb-0">No items or folders in this category</p>
                  ) : category.items?.length > 0 ? (
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={category.items?.every(item => selectedItems.has(item.id))}
                              onChange={() => {
                                const itemIds = category.items?.map(item => item.id) || [];
                                setSelectedItems(prev => {
                                  const newSet = new Set(prev);
                                  const allSelected = itemIds.every(id => newSet.has(id));
                                  if (allSelected) {
                                    itemIds.forEach(id => newSet.delete(id));
                                  } else {
                                    itemIds.forEach(id => newSet.add(id));
                                  }
                                  return newSet;
                                });
                              }}
                              title="Select all items in this section"
                            />
                          </th>
                          <th>Title</th>
                          <th>Type</th>
                          <th>Brands</th>
                          <th>Access</th>
                          <th>File/URL</th>
                          <th>Size</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {category.items?.map((item, ii) => (
                          <tr key={item.id} className={`${!item.is_active ? 'table-warning' : ''} ${selectedItems.has(item.id) ? 'table-primary' : ''}`}>
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedItems.has(item.id)}
                                onChange={() => handleSelectItem(item.id)}
                              />
                            </td>
                            <td>{item.title}</td>
                            <td>
                              <span
                                className={`badge ${
                                  item.item_type === 'link' ? 'bg-info' : 'bg-primary'
                                }`}
                              >
                                {item.item_type}
                              </span>
                            </td>
                            <td>
                              {item.visibility_mode === 'exclude' ? (
                                <span className="badge bg-info text-dark" title={`Hidden from: ${item.target_domains?.join(', ')}`}>
                                  🚫 {item.target_domains?.length || 0} hidden
                                </span>
                              ) : item.for_all_brands ? (
                                <span className="badge bg-success">All Brands</span>
                              ) : (
                                <span className="badge bg-warning text-dark" title={item.target_domains?.join(', ')}>
                                  {item.target_domains?.length || 0} brand{(item.target_domains?.length || 0) !== 1 ? 's' : ''}
                                </span>
                              )}
                            </td>
                            <td>
                              {item.is_public ? (
                                <span className="badge bg-info" title="Public download link - no login required">
                                  <i className="bi bi-globe"></i> Public
                                </span>
                              ) : (
                                <span className="badge bg-secondary" title="Private - requires login">
                                  <i className="bi bi-lock"></i> Private
                                </span>
                              )}
                            </td>
                            <td>
                              {item.item_type === 'link' ? (
                                <a
                                  href={item.external_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="small"
                                >
                                  {item.external_url?.substring(0, 40)}...
                                </a>
                              ) : (
                                <span className="small">{item.file_name}</span>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">
                                {formatFileSize(item.file_size)}
                              </small>
                            </td>
                            <td className="text-end">
                              <div className="btn-group btn-group-sm me-2" role="group">
                                <button className="btn btn-outline-secondary" title="Move up"
                                  disabled={ii === 0}
                                  onClick={() => handleMoveItem(category.items, ii, 'up')}>▲</button>
                                <button className="btn btn-outline-secondary" title="Move down"
                                  disabled={ii === category.items.length - 1}
                                  onClick={() => handleMoveItem(category.items, ii, 'down')}>▼</button>
                              </div>
                              {item.is_public && (
                                <button
                                  className="btn btn-sm btn-outline-info me-2"
                                  onClick={() => {
                                    navigator.clipboard.writeText(`${API_BASE_URL || window.location.origin}/api/resources/download/${item.id}`);
                                    setMessage({ type: 'success', text: 'Link copied!' });
                                  }}
                                  title="Copy public download link"
                                >
                                  <i className="bi bi-link-45deg"></i>
                                </button>
                              )}
                              <button
                                className="btn btn-sm btn-outline-secondary me-2"
                                onClick={() => handleEditItem(item, category)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteItem(item.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResourcesEditor;
